import React, { useState } from 'react';
import axios from 'axios';
import { Flag, X, ShieldCheck, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const REPORT_REASONS = [
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'inappropriate_behavior', label: 'Inappropriate language or behavior' },
  { value: 'inappropriate_content', label: 'Sexual or explicit content' },
  { value: 'fake_profile', label: 'Fake profile or catfishing' },
  { value: 'spam', label: 'Spam, scam, or asking for money' },
  { value: 'threats', label: 'Threats or violence' },
  { value: 'underage', label: 'Appears to be under 18' },
  { value: 'other', label: 'Something else' },
];

const ReasonList = ({ reason, setReason }) => (
  <div className="space-y-2" role="radiogroup">
    {REPORT_REASONS.map((r) => (
      <button
        key={r.value}
        type="button"
        role="radio"
        aria-checked={reason === r.value}
        onClick={() => setReason(r.value)}
        data-testid={`report-reason-${r.value}`}
        className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-colors duration-150 ${
          reason === r.value
            ? 'border-rose-500 bg-rose-50 text-rose-800 font-semibold'
            : 'border-gray-200 hover:border-rose-300 text-gray-700'
        }`}
      >
        {r.label}
      </button>
    ))}
  </div>
);

const ReportSuccess = ({ name, onClose }) => (
  <div className="text-center py-4" data-testid="report-success">
    <ShieldCheck className="w-14 h-14 mx-auto text-emerald-500 mb-4" />
    <h3 className="text-xl font-bold text-gray-900 mb-2">Report received</h3>
    <p className="text-gray-600 text-sm mb-6">
      {name} has been blocked and won't be able to contact you or appear in your matches.
      Our safety team will review this within 24 hours.
    </p>
    <button
      onClick={onClose}
      data-testid="report-done-btn"
      className="w-full bg-gray-900 hover:bg-black text-white py-3 rounded-xl font-semibold transition-colors"
    >
      Done
    </button>
  </div>
);

export const ReportUserDialog = ({ open, user, sessionId, onClose, onReported }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!open || !user) return null;

  const close = () => {
    const wasDone = done;
    setReason(''); setDetails(''); setError(''); setDone(false);
    onClose();
    if (wasDone && onReported) onReported(user);
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/safety/report`,
        { reported_user_id: user.id, reason, details: details || null, session_id: sessionId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDone(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="report-user-dialog">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 max-h-[92vh] overflow-y-auto shadow-2xl">
        {done ? <ReportSuccess name={user.name} onClose={close} /> : (
          <>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <Flag className="w-5 h-5 mr-2 text-rose-600" /> Report {user.name}
              </h3>
              <button onClick={close} data-testid="report-close-btn" className="text-gray-400 hover:text-gray-700" aria-label="Close">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              They'll be blocked right away. Your report is confidential — they won't know it was you.
            </p>
            <ReasonList reason={reason} setReason={setReason} />
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={1000}
              placeholder="Anything else our safety team should know? (optional)"
              data-testid="report-details-input"
              className="w-full mt-4 border border-gray-200 rounded-xl px-3 py-2 h-20 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-rose-400"
            />
            {error && <p className="text-sm text-red-600 mt-2" data-testid="report-error">{error}</p>}
            <button
              onClick={submit}
              disabled={!reason || submitting}
              data-testid="report-submit-btn"
              className="w-full mt-4 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold flex items-center justify-center transition-colors"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Report & Block
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportUserDialog;
